import MicIcon from '@/assets/icons/mic.svg?react'
import { Button, Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { useWavesurfer } from '@wavesurfer/react'
import clsx from 'clsx'
import { Pause, Play, SquareCheck } from 'lucide-react'
import { useRef } from 'react'
import type { RecordingQuality } from '../../hooks/useAudioRecorder'
import { formatTime } from './AudioRecordingTask'

type RecordingStateRecordedProps = {
  audioUrl: string | null
  seconds: number
  isSubmitting: boolean
  minSeconds?: number
  submitDisabled?: boolean
  quality?: RecordingQuality | null
  onReRecord: () => void
  onSubmit: () => void
}

const QualityMessage = ({ issue }: { issue: NonNullable<RecordingQuality['result']['issue']> }) => {
  switch (issue) {
    case 'no-signal':
      return (
        <Trans>
          No sound is coming from your microphone. If you opened this page inside another app, open it in Chrome or
          Safari.
        </Trans>
      )
    case 'no-voice':
      return <Trans>We could not hear your voice. Please record again and speak clearly.</Trans>
    case 'quiet':
      return <Trans>Your recording sounds very quiet. You can record again or submit anyway.</Trans>
    case 'noise':
      return <Trans>There is a lot of background noise. You can record again or submit anyway.</Trans>
    case 'clip':
      return <Trans>Your recording is too loud and may be distorted. You can record again or submit anyway.</Trans>
  }
}

export const RecordingStateRecorded = ({
  audioUrl,
  seconds,
  isSubmitting,
  minSeconds = 1,
  submitDisabled = false,
  quality,
  onReRecord,
  onSubmit
}: RecordingStateRecordedProps) => {
  const playerRef = useRef<HTMLDivElement>(null)

  const { wavesurfer, isPlaying } = useWavesurfer({
    container: playerRef,
    url: audioUrl || '',
    cursorColor: 'transparent',
    cursorWidth: 2,
    dragToSeek: false,
    duration: 10,
    height: 40,
    waveColor: '#727272',
    progressColor: '#39c900',
    autoplay: false,
    barWidth: 3,
    barHeight: 4
  })

  const onPlayPause = () => {
    wavesurfer?.playPause()
  }

  return (
    <div className="flex w-full flex-col items-center gap-6" style={{ animation: 'nectar-slide-up 0.3s ease-out' }}>
      <div className="flex flex-col items-center gap-2">
        <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
          <div className="bg-red absolute inset-0 rounded-full opacity-25" />
          <button
            type="button"
            onClick={onReRecord}
            className="bg-red relative z-1 flex size-20 items-center justify-center rounded-full text-white shadow-lg transition-all duration-200 hover:scale-105"
          >
            <MicIcon className="h-12" />
          </button>
        </div>
        <Text type="body2-regular" className="text-gray-90">
          <Trans>Re-record</Trans>
        </Text>
      </div>

      <div className="my-2 flex w-full flex-col items-center gap-2">
        {audioUrl && (
          <div className="flex w-full items-center gap-2">
            <div className={clsx('h-10 w-full flex-1')} ref={playerRef}></div>
            <div className="text-body2-regular text-gray-90 flex items-center gap-3">{formatTime(seconds)}</div>
          </div>
        )}
        {seconds < minSeconds && !isSubmitting && (
          <Text type="body3-regular" className="text-red-500">
            <Trans>Recording is too short</Trans>
          </Text>
        )}
        {quality?.result.issue && (
          <div
            className={clsx(
              'w-full rounded-xl border border-solid p-3 text-center',
              quality.blocked ? 'border-red bg-red/10' : 'border-yellow bg-yellow/10'
            )}
          >
            <Text type="body3-regular" className={quality.blocked ? 'text-red' : 'text-gray-80'}>
              <QualityMessage issue={quality.result.issue} />
            </Text>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          onClick={onPlayPause}
          variant={'tertiary'}
          size="small"
          className="hover:text-green! flex w-[115px] items-center gap-1 hover:bg-transparent!"
        >
          {isPlaying ? (
            <>
              <Pause className="h-4 w-4 shrink-0" />
              <Trans>Pause</Trans>
            </>
          ) : (
            <>
              <Play className="h-4 w-4 shrink-0" />
              <Trans>Play</Trans>
            </>
          )}
        </Button>
        <Button
          size="small"
          variant="green"
          onClick={onSubmit}
          isDisabled={isSubmitting || seconds < minSeconds || submitDisabled || quality?.blocked === true}
          className="flex w-[115px] items-center gap-1"
        >
          <SquareCheck className="h-4 w-4 shrink-0" />
          {isSubmitting ? <Trans>Sending...</Trans> : <Trans>Submit</Trans>}
        </Button>
      </div>
    </div>
  )
}
